import React, { useState, useEffect, useMemo } from "react";
import "../../ManagerDashboard.css";
import DataTable from "../../Robuts Components/DataTable";
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import Joyride, { STATUS } from "react-joyride";
import { FaQuestionCircle, FaCopy, FaCheck } from "react-icons/fa";
import CustomModal from "../../../Global/Modal/CustomModal";
import TimeInput from "react-simple-time-input";
// Group CRUD operations are now handled by parent component with real-time listeners
import {
  GOLD_PACKAGE,
  SILVER_PACKAGE,
  BRONZE_PACKAGE,
} from "../../../../backend/PAYMENT_PACKAGES_CONSTANTS";
import { FaRegCalendarPlus, FaCalendarAlt } from "react-icons/fa";
import TipCard from "../../Robuts Components/TipCard";
import GroupDetailsModal from "./GroupDetailsModal";

// --- DESIGN STYLES ---
const mainBg = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
  padding: "32px 0",
};
const containerStyle = {
  maxWidth: 1200,
  margin: "0 auto",
  padding: "0 16px",
};
// --- END DESIGN STYLES ---

function GroupsPane({
  groups,
  students,
  activateAlert,
  onGroupUpdate,
  onGroupAdd,
  onGroupDelete,
  userID,
  userData,
  ownerID,
  presenceLogs,
}) {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null, // 'details', 'form', 'delete'
    data: null,
  });

  const [runTour, setRunTour] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    dojo: "",
    description: "",
    trainingSlots: [],
    selectedDays: [],
    id: "",
    isEdited: false,
    paymentCheck: false,
    paymentUrl: "",
  });

  // Replace useState with useMemo for dojo suggestions
  const dojoSuggestions = useMemo(() => {
    const uniqueDojos = [...new Set(groups.map((group) => group.dojo))];
    return uniqueDojos.sort();
  }, [groups]);

  const [originalFormData, setOriginalFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [copyButtonState, setCopyButtonState] = useState({
    isCopied: false,
    icon: <FaCopy />,
  });

  const hasViewPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.groups?.view ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;
  const hasEditPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.groups?.write ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;

  // Simplified form validation and change detection
  useEffect(() => {
    // Basic form validation - check name, dojo, and at least one training slot
    const isValid =
      formData.name.trim() !== "" &&
      formData.dojo.trim() !== "" &&
      formData.trainingSlots.length > 0 &&
      formData.trainingSlots.every((slot) => {
        const [start, end] = slot.range.split("-");
        return start && end && start.trim() !== "" && end.trim() !== "";
      });

    // Check for changes
    let hasFormChanges = false;

    if (formData.isEdited && originalFormData) {
      // For edit mode, compare with original data
      const nameChanged = formData.name !== originalFormData.name;
      const dojoChanged = formData.dojo !== originalFormData.dojo;
      const descriptionChanged =
        formData.description !== originalFormData.description;
      const paymentCheckChanged =
        formData.paymentCheck !== originalFormData.paymentCheck;

      // Compare training slots with initial group data
      const initialGroup = modalState.data;
      const currentSlotsStr = JSON.stringify(formData.trainingSlots);
      const initialSlotsStr = JSON.stringify(initialGroup?.trainingSlots || []);
      const trainingSlotsChanged = currentSlotsStr !== initialSlotsStr;

      hasFormChanges =
        nameChanged ||
        dojoChanged ||
        descriptionChanged ||
        paymentCheckChanged ||
        trainingSlotsChanged;

      // Update hasChanges based on detected changes
      setHasChanges(hasFormChanges);
    } else if (!formData.isEdited) {
      // For new group, any field filled counts as a change
      hasFormChanges =
        formData.name.trim() !== "" ||
        formData.dojo.trim() !== "" ||
        formData.description.trim() !== "" ||
        formData.selectedDays.length > 0 ||
        formData.paymentCheck;
    }

    setIsFormValid(isValid);
  }, [formData, originalFormData, modalState.data]);

  // If user doesn't have view privilege, show message and return
  if (!hasViewPrivilege) {
    return (
      <div style={mainBg}>
        <div style={containerStyle}>
          <div className="groups-wrapper">
            <Alert
              variant="warning"
              className="m-3"
              style={{ direction: "rtl" }}
            >
              אין לך הרשאות לצפייה בקבוצות. אנא פנה למנהל המערכת לקבלת הרשאות.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  const practiceDays = [
    { id: "ש", label: "יום שבת" },
    { id: "ו", label: "יום שישי" },
    { id: "ה", label: "יום חמישי" },
    { id: "ד", label: "יום רביעי" },
    { id: "ג", label: "יום שלישי" },
    { id: "ב", label: "יום שני" },
    { id: "א", label: "יום ראשון" },
  ];

  // Tour button style
  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#0066CC",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  // Define tour steps
  const steps = [
    {
      target: ".groups-wrapper",
      content:
        "ברוכים הבאים למסך הקבוצות! כאן תוכלו לנהל את כל הקבוצות בארגון, כולל יצירה, עריכה ומחיקה של קבוצות. בואו נעשה סיור קצר!",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="add-group-button"]',
      content:
        "לחצו כאן כדי להוסיף קבוצה חדשה. תוכלו להגדיר את שם הקבוצה, סניף, ימי אימון ופרטים נוספים.",
      placement: "bottom",
    },
    {
      target: ".data-table-container",
      content:
        "כאן תוכלו לראות את כל הקבוצות הקיימות. לחצו על קבוצה כדי לראות את כל הפרטים שלה.",
      placement: "bottom",
    },
    {
      target: ".tour-guide-button",
      content: "אם תרצו לראות את הסיור שוב, תוכלו ללחוץ על כפתור זה בכל עת!",
      placement: "right",
      spotlightClicks: true,
    },
  ];

  // Handle tour events
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  // Single modal handler function
  const handleModal = (type, data = null) => {
    // Check package limits when trying to add a new group
    if (type === "form" && !data) {
      const userPackage = userData?.paymentDetails?.plan?.toLowerCase();
      let maxGroups;

      switch (userPackage) {
        case "gold":
          maxGroups = GOLD_PACKAGE.NUMBER_OF_GROUPS;
          break;
        case "silver":
          maxGroups = SILVER_PACKAGE.NUMBER_OF_GROUPS;
          break;
        case "bronze":
          maxGroups = BRONZE_PACKAGE.NUMBER_OF_GROUPS;
          break;
        default:
          maxGroups = 0;
      }

      if (groups.length >= maxGroups) {
        setModalState({
          isOpen: true,
          type: "packageLimit",
          data: { maxGroups },
        });
        return;
      }
    }

    setModalState({ isOpen: true, type, data });
  };

  // Close modal and reset states
  const closeModal = () => {
    setModalState({ isOpen: false, type: null, data: null });
    if (modalState.type === "form") {
      resetForm();
    }
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: "",
      dojo: "",
      description: "",
      trainingSlots: [],
      selectedDays: [],
      id: "",
      isEdited: false,
      paymentCheck: false,
      paymentUrl: "",
    });
  };

  // Form handlers
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day) => {
    setFormData((prev) => {
      const newSelectedDays = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter((d) => d !== day)
        : [...prev.selectedDays, day];

      const newTrainingSlots = prev.selectedDays.includes(day)
        ? prev.trainingSlots.filter((slot) => slot.day !== day)
        : [...prev.trainingSlots];

      return {
        ...prev,
        selectedDays: newSelectedDays,
        trainingSlots: newTrainingSlots,
      };
    });
  };

  const handleHourChange = (day, type, value) => {
    setFormData((prev) => {
      const updatedSlots = [...prev.trainingSlots];
      const slotIndex = updatedSlots.findIndex((slot) => slot.day === day);

      // Helper function to validate time string
      const isValidTime = (timeStr) => {
        if (!timeStr) return false;
        const [hours, minutes] = timeStr.split(":").map(Number);
        return (
          !isNaN(hours) &&
          !isNaN(minutes) &&
          hours >= 0 &&
          hours < 24 &&
          minutes >= 0 &&
          minutes < 60
        );
      };

      // Helper function to convert time string to minutes
      const timeToMinutes = (timeStr) => {
        if (!isValidTime(timeStr)) return 0;
        const [hours, minutes] = timeStr.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // Helper function to convert minutes to time string
      const minutesToTime = (minutes) => {
        if (isNaN(minutes) || minutes < 0) minutes = 0;
        if (minutes >= 1440) minutes = 1439; // 23:59
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, "0")}:${mins
          .toString()
          .padStart(2, "0")}`;
      };

      // Validate the input value
      if (!isValidTime(value)) {
        // If the input is invalid, use the current time or default to 00:00
        const currentSlot = updatedSlots[slotIndex];
        if (currentSlot) {
          const [currentStart, currentEnd] = currentSlot.range.split("-");
          value = type === "start" ? currentStart : currentEnd;
        } else {
          value = "00:00";
        }
      }

      if (slotIndex !== -1) {
        const [start, end] = updatedSlots[slotIndex].range.split("-");
        let newStart = type === "start" ? value : start;
        let newEnd = type === "end" ? value : end;

        // Convert times to minutes for comparison
        const startMinutes = timeToMinutes(newStart);
        const endMinutes = timeToMinutes(newEnd);

        if (type === "start") {
          // If start time is after current end time, set end time to 30 minutes after start
          if (startMinutes >= endMinutes) {
            newEnd = minutesToTime(startMinutes + 30);
          }
        } else if (type === "end") {
          // If end time is before or equal to start time, set start time to 30 minutes before end
          if (endMinutes <= startMinutes) {
            newStart = minutesToTime(endMinutes - 30);
          }
        }

        updatedSlots[slotIndex].range = `${newStart}-${newEnd}`;
      } else {
        // For new slots, ensure proper interval
        if (type === "start") {
          const startMinutes = timeToMinutes(value);
          updatedSlots.push({
            day,
            range: `${value}-${minutesToTime(startMinutes + 30)}`,
          });
        } else {
          const endMinutes = timeToMinutes(value);
          updatedSlots.push({
            day,
            range: `${minutesToTime(endMinutes - 30)}-${value}`,
          });
        }
      }

      return { ...prev, trainingSlots: updatedSlots };
    });
  };

  // CRUD operations
  const handleGroupFormSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    const groupData = {
      name: formData.name,
      dojo: formData.dojo,
      trainingSlots: formData.trainingSlots,
      description: formData.description,
      id: formData.id,
      paymentCheck: formData.paymentCheck,
      paymentUrl: formData.paymentUrl || null,
    };

    try {
      if (formData.isEdited) {
        await onGroupUpdate(groupData);
        activateAlert("success", "אליפות!", "הקבוצה עודכנה בהצלחה");
      } else {
        await onGroupAdd(groupData);
        activateAlert("success", "אליפות!", "הקבוצה נוצרה בהצלחה");
      }
      closeModal();
      resetForm();
    } catch (error) {
      console.error("Failed to save group:", error);
      activateAlert(
        "error",
        "אופס",
        `התרחשה תקלה ב${formData.isEdited ? "עריכת" : "יצירת"} הקבוצה`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGroupRowClick = (group) => {
    handleModal("details", group);
  };

  const copyRegistrationLink = (group) => {
    const baseUrl = "https://www.simplycoach.co/newstudent";
    const userField = userData?.userField || "default";
    const link = `הינך מוזמן להרשם באפליקציית SimplyCoach לקבוצת ה${group.name} בסניף ${group.dojo} בקישור הבא: ${baseUrl}/${userField}/${ownerID}/${group.id} \n *במהלך תהליך הרישום, הזן את קוד המאמן שלי - ABC123*`;

    navigator.clipboard.writeText(link).then(() => {
      setCopyButtonState({
        isCopied: true,
        icon: <FaCheck />,
      });
      setTimeout(() => {
        setCopyButtonState({
          isCopied: false,
          icon: <FaCopy />,
        });
      }, 2000);
    });
  };

  const handleEditGroup = (group) => {
    const originalData = {
      name: group.name,
      dojo: group.dojo,
      description: group.description,
      trainingSlots: JSON.parse(JSON.stringify(group.trainingSlots)), // Deep copy
      selectedDays: [...new Set(group.trainingSlots.map((slot) => slot.day))],
      id: group.id,
      paymentCheck: group.paymentCheck || false,
      paymentUrl: group.paymentUrl || "",
    };

    setOriginalFormData(originalData);
    setFormData({
      ...originalData,
      isEdited: true,
    });
    setHasChanges(false);
    // Open the form modal directly with the group data
    setModalState({
      isOpen: true,
      type: "form",
      data: group,
    });
  };

  const handleDeleteGroup = (group) => {
    handleModal("delete", group);
  };

  const handleGroupDelete = async () => {
    setDeletingGroupId(modalState.data.id);
    try {
      await onGroupDelete(modalState.data.id);
      activateAlert("success", "אליפות!", "הקבוצה נמחקה בהצלחה");
    } catch (error) {
      console.error("Failed to delete group:", error);
      activateAlert("error", "אופס", "לא הצלחנו למחוק את הקבוצה");
    } finally {
      setDeletingGroupId(null);
      closeModal();
    }
  };

  // Helper functions
  const getTimeByType = (day, type) => {
    const slot = formData.trainingSlots.find((slot) => slot.day === day);
    if (!slot) return null;
    const [start, end] = slot.range.split("-");
    return type === "start" ? start : type === "end" ? end : null;
  };

  // Render modals based on type
  const renderModal = () => {
    switch (modalState.type) {
      case "packageLimit":
        return (
          <CustomModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            title="אופס!"
            children={
              <p className="hebrewText">
                הגעת למקסימום הקבוצות שניתן לנהל בחבילה שלך (
                {modalState.data.maxGroups} קבוצות)
              </p>
            }
            footerButtons={[
              {
                title: "הבנתי",
                onClick: closeModal,
                style: "grey",
              },
            ]}
          />
        );

      case "details":
        return (
          <GroupDetailsModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            group={modalState.data}
            students={students}
            onCopyLink={copyRegistrationLink}
            copyButtonState={copyButtonState}
            onEdit={handleEditGroup}
            onDelete={handleDeleteGroup}
            hasEditPrivilege={hasEditPrivilege}
            userData={userData}
            groups={groups}
            presenceLogs={presenceLogs}
          />
        );

      case "delete":
        return hasEditPrivilege ? (
          <CustomModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            width={40}
            height={40}
            title="אישור מחיקת קבוצה"
            children={
              <>
                <p className="hebrewText" dir="rtl">
                  האם אתה בטוח שברצונך למחוק את קבוצת {modalState.data.name}{" "}
                  מסניף {modalState.data.dojo}?
                </p>
                <p dir="rtl">
                  <strong>שים לב, פעולה זו היא בלתי הפיכה!</strong>
                </p>
              </>
            }
            footerButtons={[
              {
                title:
                  deletingGroupId === modalState.data.id ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      מוחק...
                    </>
                  ) : (
                    "הבנתי, מחק בכל זאת"
                  ),
                onClick: handleGroupDelete,
                disabled: deletingGroupId === modalState.data.id,
                style: "red",
              },
              {
                title: "ביטול",
                onClick: closeModal,
                style: "grey",
              },
            ]}
          />
        ) : null;

      case "form":
        return hasEditPrivilege ? (
          <CustomModal
            isOpen={modalState.isOpen}
            width={80}
            onClose={closeModal}
            title={formData.isEdited ? "ערוך קבוצה" : "הוסף קבוצה חדשה"}
            children={
              <Form onSubmit={handleGroupFormSubmit}>
                <div className="form-grid">
                  <Form.Group
                    className="groupFormFieldContainer"
                    controlId="groupClubName"
                  >
                    <Form.Label dir="rtl">שם הסניף*</Form.Label>
                    <Form.Control
                      type="text"
                      className="groupFormInput"
                      dir="rtl"
                      value={formData.dojo}
                      onChange={(e) => handleFormChange("dojo", e.target.value)}
                      list="dojo-suggestions"
                      required
                    />
                    <datalist id="dojo-suggestions">
                      {dojoSuggestions.map((dojo, index) => (
                        <option key={index} value={dojo} />
                      ))}
                    </datalist>
                  </Form.Group>

                  <Form.Group
                    className="groupFormFieldContainer"
                    controlId="groupName"
                  >
                    <Form.Label className="hebrewText">שם הקבוצה*</Form.Label>
                    <Form.Control
                      type="text"
                      className="groupFormInput"
                      dir="rtl"
                      value={formData.name}
                      onChange={(e) => handleFormChange("name", e.target.value)}
                      required
                    />
                  </Form.Group>
                </div>

                <Form.Group
                  className="groupFormFieldContainer"
                  controlId="groupDescription"
                >
                  <Form.Label>תיאור הקבוצה</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className="groupFormInput"
                    dir="rtl"
                    value={formData.description}
                    onChange={(e) =>
                      handleFormChange("description", e.target.value)
                    }
                  />
                </Form.Group>

                <Form.Group
                  className="groupFormFieldContainer mt-4"
                  controlId="paymentUrl"
                >
                  <Form.Label>קישור תשלום לקבוצה</Form.Label>
                  <div className="payment-description mb-2">
                    <small className="text-muted">
                      קישור זה יופיע אוטומטית כאשר תזמין ספורטאים לקבוצה
                    </small>
                  </div>
                  <Form.Control
                    type="url"
                    className="groupFormInput"
                    dir="rtl"
                    value={formData.paymentUrl}
                    onChange={(e) =>
                      handleFormChange("paymentUrl", e.target.value)
                    }
                    placeholder="הדבק קישור"
                  />
                </Form.Group>

                <Form.Group
                  className="groupFormFieldContainer mt-4"
                  controlId="paymentCheck"
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      direction: "rtl",
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      label="בדיקת תשלום בעת הרשמה"
                      checked={formData.paymentCheck}
                      onChange={(e) =>
                        handleFormChange("paymentCheck", e.target.checked)
                      }
                      className=""
                    />
                    <div className="payment-check-description mt-1">
                      <small className="text-muted">
                        נרשמים חדשים לקבוצה יצטרכו להזין מספר חשבונית ו-4 ספרות
                        אחרונות של אמצעי תשלום. מידע זה יוצג לך כאשר תאשר את
                        הספורטאים כך שתוכל לוודא שאכן בוצע תשלום.
                      </small>
                    </div>
                  </div>
                </Form.Group>

                <Form.Group
                  className="groupFormFieldContainer mt-4"
                  controlId="practiceDays"
                >
                  <Form.Label dir="rtl">ימי אימון*</Form.Label>
                  <div className="practice-days-grid">
                    {practiceDays.reverse().map((day) => (
                      <div key={day.id} className="practice-day-card">
                        <Button
                          variant={
                            formData.selectedDays.includes(day.id)
                              ? "primary"
                              : "outline-primary"
                          }
                          className={`day-button ${formData.selectedDays.includes(day.id)
                            ? "active"
                            : ""
                            }`}
                          onClick={() => toggleDay(day.id)}
                        >
                          <div className="day-button-content">
                            <span className="day-label">{day.label}</span>
                          </div>
                        </Button>
                        {formData.selectedDays.includes(day.id) && (
                          <div className="time-inputs">
                            <TimeInput
                              onValueChange={(e) =>
                                handleHourChange(day.id, "start", e)
                              }
                              value={getTimeByType(day.id, "start") || ""}
                              clockMode={24}
                              placeholder="00:00"
                              style={{
                                width: "80px",
                                textAlign: "center",
                                padding: "4px 8px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: "#fff",
                                transition: "all 0.2s ease",
                                outline: "none",
                              }}
                            />
                            <span className="time-separator">עד</span>
                            <TimeInput
                              style={{
                                width: "80px",
                                textAlign: "center",
                                padding: "4px 8px",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                fontSize: "14px",
                                backgroundColor: "#fff",
                                transition: "all 0.2s ease",
                                outline: "none",
                              }}
                              value={getTimeByType(day.id, "end") || ""}
                              onValueChange={(e) =>
                                handleHourChange(day.id, "end", e)
                              }
                              clockMode={24}
                              placeholder="00:00"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Form.Group>
              </Form>
            }
            footerButtons={[
              {
                title: isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {formData.isEdited ? "מעדכן..." : "יוצר..."}
                  </>
                ) : (
                  "שמירה"
                ),
                onClick: handleGroupFormSubmit,
                disabled: !isFormValid || isSubmitting,
                style: isFormValid && hasChanges ? "primary" : "grey",
              },
              {
                title: "ביטול",
                onClick: closeModal,
                style: "grey",
              },
            ]}
          />
        ) : null;

      default:
        return null;
    }
  };

  // --- DESIGN STYLES ---
  const headerStyle = {
    fontWeight: 800,
    fontSize: "2.5rem",
    letterSpacing: 1,
    marginBottom: 24,
    textAlign: "center",
  };
  const sectionCardStyle = {
    marginBottom: 40,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 24,
    direction: "ltr",
  };
  const addButtonStyle = {
    fontWeight: 700,
    fontSize: "1.2rem",
    borderRadius: 999,
    padding: "16px 40px",
    boxShadow: "0 2px 8px rgba(0,180,80,0.10)",
    marginBottom: 32,
  };
  // --- END DESIGN STYLES ---

  // Modern empty state if no groups
  const hasAnyGroups = groups && groups.length > 0;

  return (
    <div style={mainBg}>
      <style>{`
        .modern-group-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-group-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-modal .modal-content {
          border-radius: 18px !important;
          background: #f8fafc !important;
        }
        .modern-modal .modal-header {
          border-radius: 18px 18px 0 0 !important;
        }
        .modern-modal .modal-footer {
          border-radius: 0 0 18px 18px !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .modern-groups-table {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          padding: 16px;
        }
      `}</style>
      <div style={containerStyle}>
        {/* Help (tour) button restored */}
        <button
          style={tourButtonStyle}
          onClick={handleTourButtonClick}
          title="הפעל סיור מודרך"
          className="tour-guide-button"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
          }}
        >
          <FaQuestionCircle size={20} />
        </button>
        <h1 style={headerStyle}>קבוצות</h1>
        {hasEditPrivilege && (
          <div style={{ textAlign: "center" }}>
            <Button
              variant="success"
              className="modern-add-btn"
              style={addButtonStyle}
              onClick={() => handleModal("form")}
              data-tour="add-group-button"
            >
              הוסף קבוצה חדשה
            </Button>
          </div>
        )}
        {/* Empty state if no groups at all */}
        {!hasAnyGroups && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              color: "#888",
              fontWeight: 600,
              fontSize: "1.3rem",
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              margin: "48px auto 0 auto",
              maxWidth: 500,
              direction: "rtl",
              padding: 48,
            }}
          >
            <FaRegCalendarPlus
              size={64}
              style={{
                marginBottom: 24,
                color: "#1976d2",
                opacity: 0.7,
              }}
            />
            לחץ למעלה כדי ליצור את הקבוצה הראשונה שלך!
          </div>
        )}
        {/* Groups Table Section */}
        {groups && groups.length === 1 && (
          <TipCard>
            כדי שספורטאים יצטרפו לקבוצה, לחץ על הקבוצה הרצויה, תעתיק את קישור
            ההרשמה ושלח אותו לספורטאים!
          </TipCard>
        )}
        {hasAnyGroups && (
          <div style={sectionCardStyle} className="modern-group-card">
            <div
              style={{
                fontWeight: 700,
                fontSize: "1.4rem",
                color: "#1976d2",
                borderRight: "6px solid #1976d2",
                paddingRight: 16,
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 10,
                direction: "rtl",
              }}
            >
              <FaCalendarAlt className="modern-section-header-icon" />
              כל הקבוצות
            </div>
            <DataTable
              data={[...groups].sort((a, b) => a.dojo.localeCompare(b.dojo))}
              type={"groups"}
              columnMapping={{
                trainingSlots: "ימי ושעות אימון",
                description: "תיאור",
                name: "שם הקבוצה",
                dojo: "שם הסניף",
              }}
              ignoredAttributes={[
                "endTime",
                "startTime",
                "id",
                "trainingDays",
                "paymentCheck",
                "paymentUrl",
              ]}
              onRowClick={handleGroupRowClick}
              data-tour="groups-table"
              className="groups-table"
            />
          </div>
        )}
        {renderModal()}
      </div>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#0066CC",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#0066CC",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
    </div>
  );
}

export default GroupsPane;
